import {
    apiCommand,
    apiQuery,
    isProblemDetails,
} from '@/modules/shared/apis/api';
import { securityApi } from '@/modules/shared/apis/securityApi';
import { useMutation, useQuery } from '@tanstack/react-query';

const useCheckPrimaryEmailStatusQuery = () => {
    return useQuery({
        queryKey: [useCheckPrimaryEmailStatusQuery.name],
        queryFn: () => apiQuery(securityApi.checkPrimaryEmailStatus()),
    });
};

const useChangePrimaryEmailCommand = () => {
    return useMutation({
        mutationKey: [useChangePrimaryEmailCommand.name],
        mutationFn: ({
            emailType,
            emailAddress,
            password,
        }: {
            emailType: string;
            emailAddress: string;
            password: string;
        }) =>
            apiCommand(
                securityApi.changePrimaryEmail(
                    emailType,
                    emailAddress,
                    password
                )
            ),
    });
};

const useConfirmPrimaryEmailCodeCommand = () => {
    return useMutation({
        mutationKey: [useConfirmPrimaryEmailCodeCommand.name],
        mutationFn: ({ verificationCode }: { verificationCode: string }) =>
            apiCommand(securityApi.confirmPrimaryEmailCode(verificationCode)),
    });
};

const useConfirmPrimaryEmailLinkCommand = () => {
    return useMutation({
        mutationKey: [useConfirmPrimaryEmailLinkCommand.name],
        mutationFn: ({
            emailVerificationId,
        }: {
            emailVerificationId: string;
        }) => apiCommand(securityApi.confirmEmailLink(emailVerificationId)),
    });
};

const useApproveNewPrimaryEmailCommand = () => {
    return useMutation({
        mutationKey: [useApproveNewPrimaryEmailCommand.name],
        mutationFn: () => apiCommand(securityApi.approveNewPrimaryEmail()),
    });
};

const useChangePasswordCommand = () => {
    return useMutation({
        mutationKey: [useChangePasswordCommand.name],
        mutationFn: ({
            newPassword,
            password,
        }: {
            newPassword: string;
            password: string;
        }) => apiCommand(securityApi.changePassword(newPassword, password)),
    });
};

const useRequestPasswordResetCommand = () => {
    return useMutation({
        mutationKey: [useRequestPasswordResetCommand.name],
        mutationFn: ({ passwordResetEmail }: { passwordResetEmail: string }) =>
            apiCommand(securityApi.requestPasswordReset(passwordResetEmail)),
    });
};

const useCompletePasswordResetCommand = () => {
    return useMutation({
        mutationKey: [useCompletePasswordResetCommand.name],
        mutationFn: ({
            passwordResetId,
            newPassword,
        }: {
            passwordResetId: string;
            newPassword: string;
        }) =>
            apiCommand(
                securityApi.completePasswordReset(passwordResetId, newPassword)
            ),
    });
};

const useCheckAlternateEmailStatusQuery = () => {
    return useQuery({
        queryKey: [useCheckAlternateEmailStatusQuery.name],
        queryFn: () => apiQuery(securityApi.checkAlternateEmailStatus()),
    });
};

const useAddAlternateEmailCommand = () => {
    return useMutation({
        mutationKey: [useAddAlternateEmailCommand.name],
        mutationFn: ({ newEmailAddress }: { newEmailAddress: string }) =>
            apiCommand(securityApi.addAlternateEmail(newEmailAddress)),
    });
};

const useRemoveAlternateEmailCommand = () => {
    return useMutation({
        mutationKey: [useRemoveAlternateEmailCommand.name],
        mutationFn: ({ emailAddress }: { emailAddress: string }) =>
            apiCommand(securityApi.removeAlternateEmail(emailAddress)),
    });
};

export const useConfirmEmailLink = () => {
    const confirmLinkCommand = useConfirmPrimaryEmailLinkCommand();

    return {
        isConfirmLinkSuccess: confirmLinkCommand.isSuccess,
        confirmLinkError: isProblemDetails(confirmLinkCommand.error)
            ? confirmLinkCommand.error.title
            : undefined,
        confirmLink: (
            emailVerificationId: string,
            props?: {
                onSuccess?: () => void;
                onError?: () => void;
            }
        ) => {
            confirmLinkCommand.mutateAsync(
                { emailVerificationId },
                {
                    onSuccess: () => {
                        props?.onSuccess?.();
                    },
                    onError: () => {
                        props?.onError?.();
                    },
                }
            );
        },
    };
};

export const useManagePrimaryEmail = () => {
    const statusQuery = useCheckPrimaryEmailStatusQuery();
    const changeCommand = useChangePrimaryEmailCommand();
    const approveCommand = useApproveNewPrimaryEmailCommand();
    const confirmCodeCommand = useConfirmPrimaryEmailCodeCommand();

    return {
        isCheckingEmailStatus: statusQuery.isLoading,
        isAwaitingApproval: statusQuery.data?.newEmail?.isAwaitingApproval,
        isAwaitingVerification:
            statusQuery.data?.newEmail?.isAwaitingVerification,
        newPrimaryEmail: statusQuery.data?.newEmail?.address,
        changeError: isProblemDetails(changeCommand.error)
            ? changeCommand.error.title
            : undefined,
        change: (
            emailType: string,
            emailAddress: string,
            password: string,
            props?: {
                onSuccess?: () => void;
                onError?: () => void;
            }
        ) => {
            changeCommand.mutateAsync(
                {
                    emailType,
                    emailAddress,
                    password,
                },
                {
                    onSuccess: () => {
                        statusQuery.refetch();
                        props?.onSuccess?.();
                    },
                    onError: () => {
                        props?.onError?.();
                    },
                }
            );
        },
        approve: () => {
            approveCommand.mutateAsync(undefined, {
                onSuccess: () => {
                    statusQuery.refetch();
                },
            });
        },
        isConfirmCodeSuccess: confirmCodeCommand.isSuccess,
        confirmCodeError: isProblemDetails(confirmCodeCommand.error)
            ? confirmCodeCommand.error.title
            : undefined,
        confirmCode: (
            verificationCode: string,
            props?: {
                onSuccess?: () => void;
                onError?: () => void;
            }
        ) => {
            confirmCodeCommand.mutateAsync(
                { verificationCode },
                {
                    onSuccess: () => {
                        props?.onSuccess?.();
                    },
                    onError: () => {
                        props?.onError?.();
                    },
                }
            );
        },
    };
};

export const useChangePassword = () => {
    const changeCommand = useChangePasswordCommand();
    const requestResetCommand = useRequestPasswordResetCommand();
    const completeResetCommand = useCompletePasswordResetCommand();

    return {
        isChangeSuccess: changeCommand.isSuccess,
        changeError: isProblemDetails(changeCommand.error)
            ? changeCommand.error.title
            : undefined,
        change: (
            newPassword: string,
            password: string,
            props?: {
                onSuccess?: () => void;
                onError?: () => void;
            }
        ) => {
            changeCommand.mutateAsync(
                {
                    newPassword,
                    password,
                },
                {
                    onSuccess: () => {
                        props?.onSuccess?.();
                    },
                    onError: () => {
                        props?.onError?.();
                    },
                }
            );
        },
        isRequestResetSuccess: requestResetCommand.isSuccess,
        requestResetError: isProblemDetails(requestResetCommand.error)
            ? requestResetCommand.error.title
            : undefined,
        requestReset: (
            passwordResetEmail: string,
            props?: {
                onSuccess?: () => void;
                onError?: () => void;
            }
        ) => {
            requestResetCommand.mutateAsync(
                { passwordResetEmail },
                {
                    onSuccess: () => {
                        props?.onSuccess?.();
                    },
                    onError: () => {
                        props?.onError?.();
                    },
                }
            );
        },
        isCompleteResetSuccess: completeResetCommand.isSuccess,
        completeResetError: isProblemDetails(completeResetCommand.error)
            ? completeResetCommand.error.title
            : undefined,
        completeReset: (
            passwordResetId: string,
            newPassword: string,
            props?: {
                onSuccess?: () => void;
                onError?: () => void;
            }
        ) => {
            completeResetCommand.mutateAsync(
                { passwordResetId, newPassword },
                {
                    onSuccess: () => {
                        props?.onSuccess?.();
                    },
                    onError: () => {
                        props?.onError?.();
                    },
                }
            );
        },
    };
};

export const useManageAlternateEmails = () => {
    const statusQuery = useCheckAlternateEmailStatusQuery();
    const addCommand = useAddAlternateEmailCommand();
    const removeCommand = useRemoveAlternateEmailCommand();

    return {
        emails: statusQuery.data?.emails ?? [],
        isAddSuccess: addCommand.isSuccess,
        addError: isProblemDetails(addCommand.error)
            ? addCommand.error.title
            : undefined,
        add: (
            email: string,
            props?: {
                onSuccess?: () => void;
                onError?: () => void;
            }
        ) => {
            addCommand.mutateAsync(
                { newEmailAddress: email },
                {
                    onSuccess: () => {
                        props?.onSuccess?.();
                        statusQuery.refetch();
                    },
                    onError: () => props?.onError?.(),
                }
            );
        },
        isRemoveSuccess: addCommand.isSuccess,
        removeError: isProblemDetails(addCommand.error)
            ? addCommand.error.title
            : undefined,
        remove: (
            email: string,
            props?: {
                onSuccess?: () => void;
                onError?: () => void;
            }
        ) => {
            removeCommand.mutateAsync(
                { emailAddress: email },
                {
                    onSuccess: () => {
                        props?.onSuccess?.();
                        statusQuery.refetch();
                    },
                    onError: () => props?.onError?.(),
                }
            );
        },
    };
};
